import IntelliProveAPIError from '../errors/intelliprove_api_exception';
import { Json } from './base';
import { UserInfo, UserInfoSchema } from '~/schemas/user_info';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { SettingsResponse, SettingsResponseSchema } from './schemas/responses/settings_response';
import { V1BucketResponse, V1BucketResponseSchema } from './schemas/responses/bucket_response';
import {
  V1BiomarkersResponse,
  V1BiomarkersResponseSchema,
  V1UnprocessableVideoResponse,
  V1UnprocessableVideoResponseSchema,
} from './schemas/responses/result_response';
import { V1BucketFeedbackResponse, V1BucketFeedbackResponseSchema } from './schemas/responses/bucket_feedback_response';
import IntelliProveNetworkError from '~services/errors/intelliprove_network_error';
import { errorEvent } from '~errors/emitter';

type Headers = Record<string, string>;

export enum AuthenticationMethod {
  ActionToken = 'actiontoken',
  Auth0 = 'auth0',
}

export default class IntelliProveAPI {
  api: AxiosInstance;
  authentication: string;
  authenticationMethod: AuthenticationMethod;

  constructor(apiurl: string, authentication: string, authenticationMethod: AuthenticationMethod = AuthenticationMethod.ActionToken) {
    this.authentication = authentication;
    this.authenticationMethod = authenticationMethod;

    this.api = axios.create({
      baseURL: apiurl,
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => this.handleError(error)
    );
  }

  private headers(contentType: string | null = null): Headers {
    let h: Headers = {};

    if (this.authenticationMethod == AuthenticationMethod.ActionToken) {
      h['authorization'] = `Token ${this.authentication}`;
    } else if ((this.authenticationMethod = AuthenticationMethod.Auth0)) {
      h['authorization'] = `Bearer ${this.authentication}`;
    }

    if (contentType !== null) {
      h['Content-Type'] = contentType;
    }

    return h;
  }

  makeUri(suburi: string, queryParams: object = {}) {
    let params: Array<string> = [];
    Object.entries(queryParams).forEach(([key, value]) => {
      // Our API requires multiple values like this
      // http://url/route?key=val1&key=val2
      if (Array.isArray(value)) {
        value.forEach((v) => params.push(`${key}=${v}`));
      } else {
        params.push(`${key}=${value}`);
      }
    });

    const queryString = '?' + params.join('&');
    return Object.keys(queryParams).length > 0 ? suburi + queryString : suburi;
  }

  async makeRequest(method: 'GET' | 'POST' | 'PUT' | 'DELETE', suburi: string, data?: Json | FormData): Promise<AxiosResponse> {
    let contentType = 'application/json';
    // Check if data is FormData (for file uploads)
    const isFileUpload = data instanceof FormData;
    if (isFileUpload) {
      contentType = 'multipart/form-data';
    }

    const headers = this.headers(contentType);

    const response = await this.api({
      method,
      url: suburi,
      data,
      headers,
    });

    return response;
  }

  async handleError(error: AxiosError): Promise<AxiosResponse> {
    if (!error.response) {
      errorEvent.emit({
        error: error.cause,
        silent: true,
        message: 'API did not return a response and status code',
        context: {
          isAxiosError: error.isAxiosError,
          axiosErrorName: error.name,
          axiosErrorMessage: error.message,
          axiosErrorStack: error.stack,
        },
      });
      throw new IntelliProveNetworkError('API did not return a reponse!');
    }

    const response = error.response;
    const body: Json = (await response.data) as Json;

    switch (response.status) {
      case 0:
        throw new IntelliProveAPIError('API did not return a reponses!', 0);

      case 401:
        throw new IntelliProveAPIError('Invalid authentication token.', response.status);

      case 403:
        throw new IntelliProveAPIError('Forbidden, you do not have access to this resource.', response.status);

      case 404:
        return response;

      case 406:
        throw new IntelliProveAPIError(body.detail[0].msg, response.status);

      case 422:
        if (response.request.responseURL && response.request.responseURL.includes('v1/results/')) {
          // Do not raise error when receiving 422 for v1/results
          return response;
        }
        throw new IntelliProveAPIError(body.detail[0].msg, response.status);

      case 500:
        throw new IntelliProveAPIError('Unknown API error', response.status);

      default:
        throw new IntelliProveAPIError('Something went wrong while trying to access the IntelliProve API.', response.status);
    }
  }

  // streaming

  async getUser(): Promise<UserInfo | null> {
    let uri = '/v1/auth/me';
    const response = await this.makeRequest('GET', uri);
    return await UserInfoSchema.parseAsync(response.data);
  }

  async getWippySettings(): Promise<SettingsResponse> {
    let uri = `/v2/wippy/settings`;
    const response = await this.makeRequest('GET', uri);
    return await SettingsResponseSchema.parseAsync(response.data);
  }

  async updateUserProfile(asynchronous: boolean): Promise<void> {
    let uri = `/v2/userprofiles?async=${asynchronous}`;
    const response = await this.makeRequest('POST', uri);
    if (response.status === 202 || response.status === 204) {
      return;
    }
    throw new IntelliProveAPIError(`Got unexpected status code for '${uri}' (Expected: 202 or 204)`, response.status);
  }

  async getV1Results(streamId: string): Promise<V1BiomarkersResponse | V1UnprocessableVideoResponse> {
    let uri = `/v1/results/${streamId}`;
    const response = await this.makeRequest('GET', uri);
    if (response.status === 200) {
      return V1BiomarkersResponseSchema.parse(response.data);
    } else if (response.status === 422) {
      return V1UnprocessableVideoResponseSchema.parse(response.data);
    }
    throw new IntelliProveAPIError('Got unexpected status code, expected 200 or 422', response.status);
  }

  async getV1Buckets(streamId: string): Promise<V1BucketResponse> {
    let uri = `/v1/buckets/all/${streamId}`;
    const response = await this.makeRequest('GET', uri);
    return V1BucketResponseSchema.parse(response.data);
  }

  async getV1BucketSummaries(streamId: string, locale: string): Promise<V1BucketFeedbackResponse> {
    let uri = `/v1/buckets/summary/${streamId}?include_tips=true&language=${locale}`;
    const response = await this.makeRequest('GET', uri);
    return V1BucketFeedbackResponseSchema.parse(response.data);
  }
}
